import type { TelemetryPort } from '@core/domain/ports/driven/TelemetryPort.ts';
import { createCorrelationId } from '@core/domain/utils/correlationId.ts';
import { AUTH_SESSION_EXPIRED_EVENT, SHELL_AUTH_LOGIN_SUCCESS_EVENT } from '@core/domain/sessionEvents.ts';
import type { ShellPorts } from '@src/core/bootstrap/ShellPorts.ts';
import type { AuthApplicationPort } from '@src/modules/auth/domain/ports/AuthApplicationPort.ts';
import type { ShellNavigationPort } from '@src/modules/shell-routing/domain/ports/ShellNavigationPort.ts';
import type { NotificationPort } from '@src/modules/platform/notifications/domain/ports/NotificationPort.ts';
import type { WebComponentsPresentationShellBase } from '@src/modules/shell-routing/infrastructure/drivers/routes/shellDomMount.ts';
import type { ShellRouteBinding } from './shellRouteBindings.ts';
import { normalizeShellPath } from '@src/modules/shell-routing/domain/shellNavConfig.ts';
import { createShellPorts } from './ShellPorts.ts';
import { Router } from '@src/modules/shell-routing/infrastructure/drivers/routes/Router.ts';
import { installCapacitorPermissionResumeHint } from '@src/modules/platform/capacitor/application/devicePermissionHints.ts';
import { installCapacitorBackButton } from '@src/modules/shell-ui/infrastructure/shellBackPress.ts';
import type { AppContainer } from './AppContainer.ts';
import { showFatalAppError } from '@src/modules/shell-ui/infrastructure/showFatalAppError.ts';
import {
  bindLocalSqlitePersistErrorHandler,
  LOCAL_SQLITE_PERSIST_ERROR_TOAST,
} from '@src/modules/platform/local-sqlite/infrastructure/localSqlitePersistError.ts';
import {
  isRecoveryMergeFailed,
  recoveryMergeFailedBannerMessage,
} from '@src/modules/platform/local-sqlite/domain/recoveryMergeFlags.ts';
import {
  notifyPostSnapshotRevisionUpgrade,
  syncTelemetryUserContext,
} from './factories/shellSessionHelpers.ts';
import {
  orchestrateAuthenticatedLogin,
  waitForLayoutComponent,
} from './factories/loginSessionOrchestrator.ts';
import { scheduleBackgroundOfflineWarmup } from './factories/offlineAssetWarmup.ts';
import { installConnectivitySyncOrchestrator } from './factories/installConnectivitySyncOrchestrator.ts';
import {
  isCatalogSyncActive,
  runPostLoginCatalogSync,
  shouldRunCatalogSyncOnLoginPage,
} from './factories/postLoginCatalogSync.ts';

export { notifyPostSnapshotRevisionUpgrade, syncTelemetryUserContext, waitForLayoutComponent };

export const PUBLIC_SHELL_PATHS: readonly string[] = ['/login'];

const UNEXPECTED_ERROR_TOAST = 'Ha ocurrido un error inesperado. Por favor, intente nuevamente.';
const BOOTSTRAP_FATAL_TITLE = 'Error al iniciar la sesión o el layout.';
const BOOTSTRAP_FATAL_HINT =
  'Revise la red, el estado de autenticación y la consola. Pulse Reintentar para recargar.';

export function isPublicShellPath(path: string): boolean {
  return PUBLIC_SHELL_PATHS.includes(normalizeShellPath(path));
}

let installedWindowErrorHandler: ((ev: ErrorEvent) => void) | null = null;
let installedUnhandledRejectionHandler: ((ev: PromiseRejectionEvent) => void) | null = null;

export type ShellRouteBindingsFactory = (
  auth: AuthApplicationPort,
  navigation: ShellNavigationPort,
  presentation: WebComponentsPresentationShellBase,
  shellPorts: ShellPorts,
) => ShellRouteBinding[];

function tryNotifyUnexpectedError(
  telemetry: TelemetryPort,
  notifications: NotificationPort | undefined,
  log: () => void,
): void {
  try {
    log();
    notifications?.error(UNEXPECTED_ERROR_TOAST);
  } catch {
    void 0;
  }
}

function reportBootstrapFailure(
  telemetry: TelemetryPort,
  error: unknown,
  source: string,
  correlationId: string,
): void {
  telemetry.logError(error instanceof Error ? error.message : String(error), {
    source,
    correlationId,
    error,
  });
  showFatalAppError({
    title: BOOTSTRAP_FATAL_TITLE,
    hint: BOOTSTRAP_FATAL_HINT,
    error,
  });
}

export function installGlobalErrorTelemetryHandlers(telemetry: TelemetryPort, notifications?: NotificationPort): void {
  if (installedWindowErrorHandler) {
    globalThis.removeEventListener('error', installedWindowErrorHandler);
    globalThis.removeEventListener('unhandledrejection', installedUnhandledRejectionHandler!);
  }

  installedWindowErrorHandler = (ev: ErrorEvent): void => {
    tryNotifyUnexpectedError(telemetry, notifications, () => {
      telemetry.logError(ev.message || 'window.error', {
        correlationId: createCorrelationId(),
        source: 'window.error',
        filename: ev.filename,
        lineno: ev.lineno,
        colno: ev.colno,
        error: ev.error,
      });
    });
  };

  installedUnhandledRejectionHandler = (ev: PromiseRejectionEvent): void => {
    const reason = ev.reason;
    // AbortError: benigno (transición/fetch cancelado al navegar, típico en Safari); sin toast.
    const benign = reason instanceof DOMException && reason.name === 'AbortError';
    tryNotifyUnexpectedError(telemetry, benign ? undefined : notifications, () => {
      telemetry.logError(reason instanceof Error ? reason : String(reason), {
        correlationId: createCorrelationId(),
        source: 'unhandledrejection',
        reasonType: typeof reason,
        stack: reason instanceof Error ? reason.stack : undefined,
      });
    });
  };

  globalThis.addEventListener('error', installedWindowErrorHandler);
  globalThis.addEventListener('unhandledrejection', installedUnhandledRejectionHandler);
}

export function resetGlobalErrorTelemetryHandlersForTests(): void {
  if (!installedWindowErrorHandler) return;
  globalThis.removeEventListener('error', installedWindowErrorHandler);
  globalThis.removeEventListener('unhandledrejection', installedUnhandledRejectionHandler!);
  installedWindowErrorHandler = null;
  installedUnhandledRejectionHandler = null;
}

export function installSessionExpiredListener(
  auth: AuthApplicationPort,
  isPublicPath: (path: string) => boolean,
  onSessionExpired?: () => void | Promise<void>,
): () => void {
  const handler = (): void => {
    const path = globalThis.location.pathname;
    if (path === '/login') return;
    void (async () => {
      await auth.logout().catch((err) => {
        // oxlint-disable-next-line no-console -- session-expired logout without strong guarantees
        console.warn('[session] logout tras expiración falló', err);
      });
      if (onSessionExpired) {
        try {
          await onSessionExpired();
        } catch (err) {
          // oxlint-disable-next-line no-console -- catalog sync is best-effort before redirect
          console.warn('[session] sync catálogos tras expiración falló', err);
        }
      }
      if (isPublicPath(path)) return;
      globalThis.location.replace(`${globalThis.location.origin}/login`);
    })();
  };
  globalThis.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handler);
  return () => globalThis.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handler);
}

export function notifyAuthLoginSuccess(): void {
  globalThis.dispatchEvent(new CustomEvent(SHELL_AUTH_LOGIN_SUCCESS_EVENT));
}

export function installAuthLoginSuccessListener(onLoginSuccess: () => void): () => void {
  globalThis.addEventListener(SHELL_AUTH_LOGIN_SUCCESS_EVENT, onLoginSuccess);
  return () => globalThis.removeEventListener(SHELL_AUTH_LOGIN_SUCCESS_EVENT, onLoginSuccess);
}

export function gateUnauthenticatedDeepLink(
  presentation: WebComponentsPresentationShellBase,
  shellPorts: ShellPorts,
  isAuth: boolean,
  currentPath: string,
): boolean {
  if (!isAuth && !isPublicShellPath(currentPath)) {
    presentation.mountLoginInApp(shellPorts);
    globalThis.history.replaceState({}, '', '/login');
    return true;
  }
  return false;
}

export function gateLoginPageOnly(
  presentation: WebComponentsPresentationShellBase,
  shellPorts: ShellPorts,
  isAuth: boolean,
  currentPath: string,
): boolean {
  if (isAuth && currentPath === '/login') {
    globalThis.history.replaceState({}, '', '/');
    return false;
  }
  if (!isAuth && currentPath === '/login') {
    presentation.mountLoginInApp(shellPorts);
    return true;
  }
  return false;
}

export async function ensureLayoutAndMainIfNeeded(
  presentation: WebComponentsPresentationShellBase,
  currentPath: string,
  telemetry: TelemetryPort,
  shellPorts: ShellPorts,
): Promise<void> {
  if (currentPath === '/login') return;

  if (!document.getElementById('app')) {
    telemetry.logError('App element not found', {});
    return;
  }
  await presentation.mountLayoutRootInApp(shellPorts);

  let main = await presentation.getLayoutPort().getMainContentHost();
  if (main) return;

  telemetry.logError('Main element not accessible, retrying', {});
  await new Promise((resolve) => setTimeout(resolve, 200));
  main = await presentation.getLayoutPort().getMainContentHost();
  if (!main) {
    telemetry.logError('Main element still not accessible after retry', {});
  }
}

export class ApplicationBootstrap {
  private readonly router: Router;
  private routerStarted = false;
  private detachCapacitorBackButton: (() => void) | null = null;
  private detachCapacitorPermissionResumeHint: (() => void) | null = null;
  private detachConnectivitySync: (() => void) | null = null;

  constructor(
    private readonly presentation: WebComponentsPresentationShellBase,
    private readonly container: AppContainer,
    private readonly shellRouteBindings: ShellRouteBindingsFactory,
  ) {
    this.router = new Router(this.container.getTelemetry(), createCorrelationId);
  }

  run(): void {
    const auth = this.container.getAuthService();
    const telemetry = this.container.getTelemetry();

    const notifications = this.container.getNotificationPort();
    installGlobalErrorTelemetryHandlers(telemetry, notifications);
    this.router.setRouteErrorHandler((message) => notifications.error(message));
    bindLocalSqlitePersistErrorHandler((err) => {
      telemetry.logError(err instanceof Error ? err.message : String(err), {
        source: 'local-sqlite.persist',
        correlationId: createCorrelationId(),
        error: err,
      });
      notifications.show(LOCAL_SQLITE_PERSIST_ERROR_TOAST, 'error', undefined, {
        label: 'Observabilidad',
        href: '/observability',
      });
    });
    this.detachCapacitorPermissionResumeHint?.();
    this.detachCapacitorPermissionResumeHint = installCapacitorPermissionResumeHint(notifications);
    this.detachConnectivitySync?.();
    this.detachConnectivitySync = installConnectivitySyncOrchestrator({
      auth,
      system: this.container.getSystemServices(),
      notifications,
      isCatalogSyncBusy: isCatalogSyncActive,
    });
    this.mountShellRoutes(auth);
    installSessionExpiredListener(auth, isPublicShellPath);

    const kickoff = () => void this.onDomContentLoaded(auth);
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', kickoff);
    } else {
      kickoff();
    }
  }

  private getShellPorts(auth: AuthApplicationPort): ShellPorts {
    return createShellPorts({
      container: this.container,
      auth,
      navigation: this.router,
      layout: this.presentation.getLayoutPort(),
      notifyLoginSuccess: () => this.onLoginSuccess(),
    });
  }

  /** Login from `/login`: mount shell, then catalog sync (deduped). */
  onLoginSuccess(): Promise<void> {
    globalThis.dispatchEvent(new CustomEvent(SHELL_AUTH_LOGIN_SUCCESS_EVENT));
    return this.continueAfterLogin();
  }

  private mountShellRoutes(auth: AuthApplicationPort): void {
    const shellPorts = this.getShellPorts(auth);
    this.shellRouteBindings(auth, this.router, this.presentation, shellPorts).forEach(({ path, handler }) =>
      this.router.addRoute(path, handler),
    );
  }

  private async finishAuthenticatedShell(auth: AuthApplicationPort, bootCorrelationId: string): Promise<void> {
    const telemetry = this.container.getTelemetry();
    const shellPorts = this.getShellPorts(auth);

    await ensureLayoutAndMainIfNeeded(this.presentation, globalThis.location.pathname, telemetry, shellPorts);
    notifyPostSnapshotRevisionUpgrade(shellPorts);
    if (isRecoveryMergeFailed()) {
      const msg = recoveryMergeFailedBannerMessage();
      if (msg) shellPorts.notifications.warning(msg);
    }
    telemetry.logInfo('Bootstrap: router', { correlationId: bootCorrelationId });
    this.startRouterOnce();
  }

  private startRouterOnce(): void {
    if (this.routerStarted) return;
    this.routerStarted = true;
    this.router.start();
    this.detachCapacitorBackButton?.();
    this.detachCapacitorBackButton = installCapacitorBackButton(() => this.router.getCurrentPath());
  }

  /** Best-effort catalog pull while login form is visible (deduped with post-login sync). */
  private prefetchCatalogsOnLoginPage(): void {
    if (!shouldRunCatalogSyncOnLoginPage()) return;
    void runPostLoginCatalogSync(this.container.getSystemServices(), this.container.getNotificationPort(), {
      pullOnly: true,
      blockOnWarmupFailure: false,
      warnIfOffline: false,
    }).catch(() => {});
  }

  private async continueAfterLogin(): Promise<void> {
    const telemetry = this.container.getTelemetry();
    const bootCorrelationId = createCorrelationId();

    try {
      await orchestrateAuthenticatedLogin({
        auth: this.container.getAuthService(),
        telemetry,
        notifications: this.container.getNotificationPort(),
        system: this.container.getSystemServices(),
        presentation: this.presentation,
        getShellPorts: (auth) => this.getShellPorts(auth),
        isRouterStarted: () => this.routerStarted,
        startRouter: () => this.startRouterOnce(),
        navigateHome: () => this.router.navigate('/'),
      });
    } catch (e) {
      reportBootstrapFailure(telemetry, e, 'bootstrap.afterLogin', bootCorrelationId);
    }
  }

  private async onDomContentLoaded(auth: AuthApplicationPort): Promise<void> {
    const telemetry = this.container.getTelemetry();
    const bootCorrelationId = createCorrelationId();
    telemetry.logInfo('Bootstrap: inicio', { correlationId: bootCorrelationId });
    const bootTrace = telemetry.startTrace('app_boot', { correlationId: bootCorrelationId });
    try {
      await auth.initializeSession();
      const isAuth = await auth.isAuthenticated();
      const currentPath = globalThis.location.pathname;

      await syncTelemetryUserContext(auth, telemetry, isAuth);

      const shellPorts = this.getShellPorts(auth);
      const onLoginPage =
        gateUnauthenticatedDeepLink(this.presentation, shellPorts, isAuth, currentPath) ||
        gateLoginPageOnly(this.presentation, shellPorts, isAuth, currentPath);
      if (onLoginPage) {
        this.prefetchCatalogsOnLoginPage();
        return;
      }

      await this.finishAuthenticatedShell(auth, bootCorrelationId);
      if (isAuth) {
        scheduleBackgroundOfflineWarmup({ entregaCatalog: shellPorts.entrega.catalog });
      }
    } catch (e) {
      reportBootstrapFailure(telemetry, e, 'bootstrap.dom', bootCorrelationId);
    } finally {
      bootTrace.end();
    }
  }
}
